import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Clock } from 'lucide-react';
import RichTextEditor from '../common/RichTextEditor';
import { getSession } from '../../getSession';
import { getFollowUpStatus, getProductsByName, getUserFromHashedList } from '../../utils/lead';

const AddFollowupForm = React.forwardRef(({ selectedCount = 0 }, ref) => {
    const [formData, setFormData] = useState({
        followupType: '',
        assignTo: 'Abhi01 (Abhishek Kumar)',
        nextStatusDate: new Date().toISOString().split('T')[0],
        nextStatusTime: '01:58 AM',
        conversionDate: new Date().toISOString().split('T')[0],
        conversionTime: '01:59 AM',
        amountPaid: '',
        remarks: '',
        products: '',
        reAssign: false,
        onSchedule: false
    });

    const [selectedProducts, setSelectedProducts] = useState([]);

    const defaultFollowups = [{ value: '', label: 'Select FollowupStatus' }];
    const [followupTypes, setFollowupTypes] = useState(defaultFollowups);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Products search state
    const [productQuery, setProductQuery] = useState(formData.products || '');
    const [productSuggestions, setProductSuggestions] = useState([]);
    const [isSearchingProducts, setIsSearchingProducts] = useState(false);
    const [showProductSuggestions, setShowProductSuggestions] = useState(false);
    const productDebounceRef = useRef(null);

    const [owners, setOwners] = useState([]);
    const [isUsersLoading, setIsUsersLoading] = useState(false);

    const fetchProductSuggestions = async (q) => {
        if (!q || String(q).trim().length === 0) {
            setProductSuggestions([]);
            return;
        }
        setIsSearchingProducts(true);
        try {
            const { userId, TokenId } = getSession();
            const payload = {
                Token: TokenId,
                Message: '',
                LoggedUserId: userId,
                MAC_Address: '',
                IP_Address: '',
                Details: { stName: q, stUserID: 0 },
                BroadcastName: ''
            };
            const resp = await getFollowUpStatus; // placeholder to avoid lint -- will be replaced below
        } catch (err) {
            console.warn('product search failed', err);
        } finally {
            setIsSearchingProducts(false);
        }
    };

    const [showNextStatusDatePicker, setShowNextStatusDatePicker] = useState(false);
    const [showConversionDatePicker, setShowConversionDatePicker] = useState(false);
    const [showNextStatusTimePicker, setShowNextStatusTimePicker] = useState(false);
    const [showConversionTimePicker, setShowConversionTimePicker] = useState(false);

    const nextStatusDatePickerRef = useRef(null);
    const conversionDatePickerRef = useRef(null);
    const nextStatusTimePickerRef = useRef(null);
    const conversionTimePickerRef = useRef(null);

    // Close date pickers on outside click
    React.useEffect(() => {
        const handler = (e) => {
            if (nextStatusDatePickerRef.current && !nextStatusDatePickerRef.current.contains(e.target)) {
                setShowNextStatusDatePicker(false);
            }
            if (conversionDatePickerRef.current && !conversionDatePickerRef.current.contains(e.target)) {
                setShowConversionDatePicker(false);
            }
            if (nextStatusTimePickerRef.current && !nextStatusTimePickerRef.current.contains(e.target)) {
                setShowNextStatusTimePicker(false);
            }
            if (conversionTimePickerRef.current && !conversionTimePickerRef.current.contains(e.target)) {
                setShowConversionTimePicker(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const formatDateDisplay = (val) => {
        if (!val) return '';
        // if value is already ISO date (YYYY-MM-DD) or Date string
        const dt = new Date(val);
        if (isNaN(dt.getTime())) {
            // try parsing YYYY-MM-DD manually
            const parts = String(val).split('-');
            if (parts.length === 3) {
                const [y, m, d] = parts;
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const mi = parseInt(m, 10) - 1;
                const mm = months[mi] || '';
                return `${String(d).padStart(2, '0')}-${mm}-${y}`;
            }
            return String(val);
        }
        const dd = String(dt.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const m = months[dt.getMonth()] || '';
        const yyyy = dt.getFullYear();
        return `${dd}-${m}-${yyyy}`;
    };

    useEffect(() => {
        let mounted = true;
        const fetchFollowups = async () => {
            try {
                const { userId, TokenId, parentId } = getSession();
                const payload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: { ParentId: parentId },
                    BroadcastName: ''
                };
                const resp = await getFollowUpStatus(payload);
                // resp expected as array of objects with Id and Text
                const arr = Array.isArray(resp) ? resp : (resp?.Details && Array.isArray(resp.Details) ? resp.Details : []);
                const opts = arr.map(i => ({ value: String(i?.Id ?? i?.Value ?? i?.Code ?? ''), label: (i?.Text ?? i?.FollowupStatus ?? i?.Name ?? String(i?.Id ?? '')) }));
                if (mounted && opts.length) {
                    setFollowupTypes([{ value: '', label: 'Select FollowupStatus' }, ...opts]);
                    // set default selection if none
                    setFormData(prev => ({ ...prev, followupType: prev.followupType || '' }));
                }
            } catch (err) {
                console.warn('Failed to load followup types', err);
            }
        };
        fetchFollowups();
        // fetch owners/collaborators
        const fetchUsers = async () => {
            setIsUsersLoading(true);
            try {
                const { userId, TokenId } = getSession();
                const payload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: { UserId: userId },
                    BroadcastName: ''
                };
                const resp = await getUserFromHashedList(payload);
                let raw = resp?.Details ?? resp?.d ?? resp ?? [];
                if (raw && raw.data) raw = raw.data;
                if (raw && !Array.isArray(raw)) {
                    raw = raw.ResultList ?? raw.Users ?? raw.UserList ?? raw.Data ?? raw.Items ?? raw;
                }
                const usersArray = Array.isArray(raw) ? raw : [];
                const mapToOption = (u) => {
                    const id = u.Code ?? u.Id ?? u.ID ?? u.UserId ?? u.Value ?? u.ValueId ?? '';
                    const label = u.Text ?? u.TextN ?? u.Name ?? u.FullName ?? u.DisplayName ?? u.LoginName ?? String(id);
                    return { value: String(id), label: String(label) };
                };
                if (mounted) {
                    const options = usersArray.map(mapToOption);
                    setOwners(options);
                    if ((!formData.assignTo || formData.assignTo === 'Abhi01 (Abhishek Kumar)') && options.length) {
                        setFormData(prev => ({ ...prev, assignTo: options[0].value }));
                    }
                }
            } catch (err) {
                console.warn('fetch users error:', err);
            } finally {
                if (mounted) setIsUsersLoading(false);
            }
        };
        fetchUsers();
        return () => { mounted = false; };
    }, []);

    // expose getFormData to parent
    useImperativeHandle(ref, () => ({
        getFormData: () => {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const parseDateToDDMMMYYYY = (d) => {
                if (!d) return '';
                // expected d like YYYY-MM-DD
                try {
                    const parts = String(d).split('-');
                    if (parts.length === 3) {
                        const [y, m, day] = parts;
                        const mi = parseInt(m, 10) - 1;
                        const mm = months[mi] || '';
                        return `${String(day).padStart(2, '0')}-${mm}-${y}`;
                    }
                    const dt = new Date(d);
                    if (!isNaN(dt.getTime())) {
                        const dd = String(dt.getDate()).padStart(2, '0');
                        const m = months[dt.getMonth()] || '';
                        const yyyy = dt.getFullYear();
                        return `${dd}-${m}-${yyyy}`;
                    }
                } catch (e) { }
                return String(d);
            };

            const to24 = (timeStr) => {
                if (!timeStr) return '';
                const t = String(timeStr).trim();
                // if already HH:mm
                const hhmmMatch = t.match(/^(\d{1,2}):(\d{2})$/);
                if (hhmmMatch) return `${String(hhmmMatch[1]).padStart(2, '0')}:${hhmmMatch[2]}`;
                const parts = t.split(' ');
                if (parts.length === 2) {
                    const [timePart, ampm] = parts;
                    const [h, m] = timePart.split(':').map(n => parseInt(n, 10));
                    if (isNaN(h) || isNaN(m)) return timePart;
                    let hh = h;
                    const a = (ampm || '').toUpperCase();
                    if (a === 'PM' && hh < 12) hh += 12;
                    if (a === 'AM' && hh === 12) hh = 0;
                    return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                }
                return t;
            };

            const followUpStatusId = parseInt(formData.followupType || '0', 10) || 0;
            const assignedTo = parseInt(formData.assignTo || '0', 10) || 0;
            // combine date + time
            let nextStatus = '';
            if (formData.nextStatusDate) {
                const dpart = parseDateToDDMMMYYYY(formData.nextStatusDate);
                const tpart = to24(formData.nextStatusTime || '');
                if (dpart && tpart) nextStatus = `${dpart} ${tpart}:00`;
            }

            // Combine selected products into a comma-separated string
            const combinedProducts = selectedProducts.map(p => p.text).join(', ');

            const obj = {
                LeadId: formData.LeadId || 0,
                AssignedTo: assignedTo,
                FollowUpStatus: followUpStatusId,
                NextStatusDate: nextStatus || '',
                Products: combinedProducts || formData.products || '',
                AmountPaid: formData.amountPaid || 0,
                Remarks: formData.remarks || '',
                IsReAssign: !!formData.reAssign,
                NotifyBySMS: false,
                NotifyByEmail: false,
                SmsScheduelDateTime: '',
                EmailScheduelDateTime: ''
            };

            return {
                objLeadFollowup_BO: obj,
                strDLTTemplateID: '',
                strUserID: '',
                strTimeZone: '',
                strParentID: ''
            };
        }
    }));

    // Debounced product search effect
    useEffect(() => {
        if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
        if (!productQuery || String(productQuery).trim().length === 0) {
            setProductSuggestions([]);
            return;
        }
        productDebounceRef.current = setTimeout(async () => {
            try {
                setIsSearchingProducts(true);
                const { userId, TokenId } = getSession();
                const payload = {
                    Token: TokenId,
                    Message: '',
                    LoggedUserId: userId,
                    MAC_Address: '',
                    IP_Address: '',
                    Details: { stName: productQuery, stUserID: userId },
                    BroadcastName: ''
                };
                const resp = await getProductsByName(payload);
                const arr = Array.isArray(resp) ? resp : (resp?.Details && Array.isArray(resp.Details) ? resp.Details : []);
                const mapped = arr.map(i => ({ id: i?.Id ?? i?.ProductID ?? i?.ProductId ?? i?.Id ?? '', text: i?.Text ?? i?.Name ?? i?.ProductName ?? String(i?.Id ?? '') }));
                setProductSuggestions(mapped);
                setShowProductSuggestions(true);
            } catch (err) {
                console.warn('getProductsByName error', err);
            } finally {
                setIsSearchingProducts(false);
            }
        }, 350);

        return () => { if (productDebounceRef.current) clearTimeout(productDebounceRef.current); };
    }, [productQuery]);

    const handleProductInputChange = (e) => {
        const v = e.target.value;
        setProductQuery(v);
        // clear selection if user changes text
        setFormData(prev => ({ ...prev, products: v, productId: prev.productId ? null : prev.productId }));
    };

    const selectProduct = (p) => {
        if (!selectedProducts.find(item => item.id === p.id)) {
            setSelectedProducts(prev => [...prev, p]);
        }
        setProductQuery('');
        setShowProductSuggestions(false);
    };

    const removeProduct = (id) => {
        setSelectedProducts(prev => prev.filter(p => p.id !== id));
    };

    const renderProductField = () => {
        return (
            <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Choose Products</label>
                <div className="relative">
                    <input
                        type="text"
                        name="products"
                        value={productQuery}
                        onChange={handleProductInputChange}
                        onFocus={() => setShowProductSuggestions(true)}
                        placeholder="Enter product"
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showProductSuggestions && productSuggestions.length > 0 && (
                        <div className="absolute left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded shadow max-h-40 overflow-auto">
                            {productSuggestions.map(p => (
                                <div
                                    key={p.id || p.text}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        selectProduct(p);
                                    }}
                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                >
                                    {p.text}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {/* Selected Products Tags */}
                <div className="mt-2 flex flex-wrap gap-2">
                    {selectedProducts.map(p => (
                        <span
                            key={p.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-300 text-gray-700 rounded text-sm shadow-sm"
                        >
                            {p.text}
                            <button
                                type="button"
                                onClick={() => removeProduct(p.id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    const timeOptions = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 30) {
            const hh = h % 12 || 12;
            const ampm = h < 12 ? 'AM' : 'PM';
            const mm = String(m).padStart(2, '0');
            timeOptions.push(`${String(hh).padStart(2, '0')}:${mm} ${ampm}`);
        }
    }

    const renderFormFields = () => {
        const currentFollowupText = (followupTypes.find(f => String(f.value) === String(formData.followupType)) || {}).label || '';
        switch (currentFollowupText) {
            case 'Call-Back':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Assign To</label>
                            <select
                                name="assignTo"
                                value={formData.assignTo}
                                onChange={handleChange}
                                className="w-full px-3 py-2 bg-gray-500 text-white rounded border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {isUsersLoading ? (
                                    <option value="">Loading...</option>
                                ) : owners.length ? (
                                    owners.map(o => <option key={o.value} value={o.value}>{o.label}</option>)
                                ) : (
                                    <option value="">No users</option>
                                )}
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Next Status Date</label>
                            <div className="relative" ref={nextStatusDatePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    value={formData.nextStatusDate ? formatDateDisplay(formData.nextStatusDate) : ''}
                                    onClick={() => setShowNextStatusDatePicker(prev => !prev)}
                                    placeholder="Select date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                {showNextStatusDatePicker && (
                                    <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow p-2">
                                        <input
                                            type="date"
                                            name="nextStatusDate"
                                            value={formData.nextStatusDate}
                                            onChange={(e) => { setFormData(prev => ({ ...prev, nextStatusDate: e.target.value })); setShowNextStatusDatePicker(false); }}
                                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Next Status Time</label>
                            <div className="relative" ref={nextStatusTimePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    name="nextStatusTime"
                                    value={formData.nextStatusTime}
                                    onClick={() => setShowNextStatusTimePicker(prev => !prev)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                <Clock className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                                {showNextStatusTimePicker && (
                                    <div className="absolute z-50 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow max-h-48 overflow-y-auto w-40">
                                        {timeOptions.map(t => (
                                            <div
                                                key={t}
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, nextStatusTime: t }));
                                                    setShowNextStatusTimePicker(false);
                                                }}
                                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm ${formData.nextStatusTime === t ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-700'}`}
                                            >
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                            <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder="Remarks"
                                rows="3"
                            />
                        </div>

                        {renderProductField()}

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Followup Type :</label>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="reAssign"
                                    checked={formData.reAssign}
                                    onChange={handleChange}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label className="ml-2 text-sm text-gray-700">Re-Assign</label>
                            </div>
                        </div>
                    </>
                );

            case 'Converted':
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Assign To</label>
                            <select
                                name="assignTo"
                                value={formData.assignTo}
                                onChange={handleChange}
                                className="w-full px-3 py-2 bg-gray-500 text-white rounded border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option>Abhi01 (Abhishek Kumar)</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Conversion Date</label>
                            <div className="relative" ref={conversionDatePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    value={formData.conversionDate ? formatDateDisplay(formData.conversionDate) : ''}
                                    onClick={() => setShowConversionDatePicker(prev => !prev)}
                                    placeholder="Select date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                {showConversionDatePicker && (
                                    <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow p-2">
                                        <input
                                            type="date"
                                            name="conversionDate"
                                            value={formData.conversionDate}
                                            onChange={(e) => { setFormData(prev => ({ ...prev, conversionDate: e.target.value })); setShowConversionDatePicker(false); }}
                                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Conversion Time</label>
                            <div className="relative" ref={conversionTimePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    name="conversionTime"
                                    value={formData.conversionTime}
                                    onClick={() => setShowConversionTimePicker(prev => !prev)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                <Clock className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                                {showConversionTimePicker && (
                                    <div className="absolute z-50 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow max-h-48 overflow-y-auto w-40">
                                        {timeOptions.map(t => (
                                            <div
                                                key={t}
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, conversionTime: t }));
                                                    setShowConversionTimePicker(false);
                                                }}
                                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm ${formData.conversionTime === t ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-700'}`}
                                            >
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Amount paid</label>
                            <input
                                type="text"
                                name="amountPaid"
                                value={formData.amountPaid}
                                onChange={handleChange}
                                placeholder="Amount Paid"
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                            <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder="Remarks"
                                rows="3"
                            />
                        </div>

                        {renderProductField()}

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Followup Type :</label>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="reAssign"
                                    checked={formData.reAssign}
                                    onChange={handleChange}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label className="ml-2 text-sm text-gray-700">Re-Assign</label>
                            </div>
                        </div>
                    </>
                );

            case 'Dead Lead':
            case 'Not-Interested':
            case 'Wrong Number':
                return (
                    <div className="mb-4">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            placeholder="Remarks"
                            rows="3"
                        />
                    </div>
                );

            default:
                return (
                    <>
                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Assign To</label>
                            <select
                                name="assignTo"
                                value={formData.assignTo}
                                onChange={handleChange}
                                className="w-full px-3 py-2 bg-gray-500 text-white rounded border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option>Abhi01 (Abhishek Kumar)</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Next Status Date</label>
                            <div className="relative" ref={nextStatusDatePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    value={formData.nextStatusDate ? formatDateDisplay(formData.nextStatusDate) : ''}
                                    onClick={() => setShowNextStatusDatePicker(prev => !prev)}
                                    placeholder="Select date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                {showNextStatusDatePicker && (
                                    <div className="absolute z-50 left-0 top-full mt-1 bg-white border border-gray-200 rounded shadow p-2">
                                        <input
                                            type="date"
                                            name="nextStatusDate"
                                            value={formData.nextStatusDate}
                                            onChange={(e) => { setFormData(prev => ({ ...prev, nextStatusDate: e.target.value })); setShowNextStatusDatePicker(false); }}
                                            className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Next Status Time</label>
                            <div className="relative" ref={nextStatusTimePickerRef}>
                                <input
                                    type="text"
                                    readOnly
                                    name="nextStatusTime"
                                    value={formData.nextStatusTime}
                                    onClick={() => setShowNextStatusTimePicker(prev => !prev)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                                />
                                <Clock className="absolute right-3 top-2.5 w-5 h-5 text-gray-400 pointer-events-none" />
                                {showNextStatusTimePicker && (
                                    <div className="absolute z-50 right-0 top-full mt-1 bg-white border border-gray-200 rounded shadow max-h-48 overflow-y-auto w-40">
                                        {timeOptions.map(t => (
                                            <div
                                                key={t}
                                                onClick={() => {
                                                    setFormData(prev => ({ ...prev, nextStatusTime: t }));
                                                    setShowNextStatusTimePicker(false);
                                                }}
                                                className={`px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm ${formData.nextStatusTime === t ? 'bg-blue-100 text-blue-700 font-semibold' : 'text-gray-700'}`}
                                            >
                                                {t}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                            <textarea
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                placeholder="Remarks"
                                rows="3"
                            />
                        </div>

                        {renderProductField()}

                        <div className="mb-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Followup Type :</label>
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="reAssign"
                                    checked={formData.reAssign}
                                    onChange={handleChange}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label className="ml-2 text-sm text-gray-700">Re-Assign</label>
                            </div>
                        </div>
                    </>
                );
        }
    };

    return (
        <div>
            {/* Info Banner - Show when mass action */}
            {selectedCount > 0 && (
                <div className="bg-blue-50 border-l-4 border-blue-500 px-4 py-3 mb-4 flex items-center justify-between">
                    <p className="text-sm text-blue-700">
                        <strong>Info!</strong> You are about to perform a mass action on {selectedCount} selected record(s)
                    </p>
                </div>
            )}

            <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Select Follow up</label>
                <select
                    name="followupType"
                    value={formData.followupType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    {followupTypes.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {renderFormFields()}

            {/* On Schedule Date And Time checkbox - Show for certain types */}
            {(formData.followupType === 'Call-Back' || formData.followupType === 'Converted') && (
                <div className="flex items-center mt-6">
                    <input
                        type="checkbox"
                        name="onSchedule"
                        checked={formData.onSchedule}
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="ml-2 text-sm text-gray-700">On Schedule Date And Time</label>
                </div>
            )}
        </div>
    );
});

export default AddFollowupForm;
